import { createAsyncThunk } from '@reduxjs/toolkit'
import { Auth, type CognitoUser } from '@aws-amplify/auth'
import axiosInstance, { setAccessToken } from 'api/apiCommon'
import { refreshToken } from 'features/authentication/auth.service'
import { type GeneratedFile } from 'common/files/files.types'

import { setUser } from '../authentication/auth.slice'
import { type LookupItem } from '../lookup/lookup.types'
import { type PaginatedResponse } from '../../common/utils/types'
import axios from '../../api/apiCommon'

import {
  type VendorAdditionalInfo,
  type Vendor,
  type VendorCompany,
  type VendorStatus,
  type DecisionDetails,
  type MyOffer,
  type GetMyOffer,
} from './vendor.types'

export const initializeVendor = createAsyncThunk(
  'vendor/initializeVendor',
  async (_, { dispatch }): Promise<Vendor> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<Vendor>('/users/vendor-info')
        .then(async (res) => {
          if (res) {
            return await Auth.currentAuthenticatedUser({ bypassCache: true })
              .then(async (user: CognitoUser) => {
                const token = await refreshToken()
                setAccessToken(token)
                dispatch(setUser(user))
                resolve({ ...res, creationDate: res.creationDate ? new Date(res.creationDate) : undefined })
              })
              .catch(() => {
                // no current user
                return null
              })
          } else {
            reject(new Error('vendor info not fetched'))
          }
        })
        .catch((e) => {
          reject(e)
        })
    })
  },
)

export const getVendorInfo = createAsyncThunk('vendor/vendorInfo', async (): Promise<Vendor> => {
  return await new Promise((resolve, reject) => {
    axiosInstance
      .get<VendorCompany>(`/users/vendor-info`)
      .then((data) => {
        resolve(data)
      })
      .catch((err) => {
        console.error(err)
        reject(err)
      })
  })
})

export const editVendor = createAsyncThunk(
  'vendor/editVendor',
  async (
    data: Pick<Partial<VendorAdditionalInfo>, 'description' | 'topics' | 'industries'> & {
      contactPerson?: {
        firstName: string
        lastName: string
        role: string
        phone: string
        email: string
      }
    },
  ) => {
    await axiosInstance.put('/vendors/edit-vendor-company', data)
    return data
  },
)
interface EditVendorSpecialtiesPayload {
  specialities: LookupItem[]
}
export const editVendorSpecialties = createAsyncThunk(
  'vendor/editVendorSpecialties',
  async (data: EditVendorSpecialtiesPayload) => {
    await axiosInstance.put('/vendors/edit-vendor-specialities', {
      specialities: data.specialities.map((specialty) => specialty.id),
    })
    return data
  },
)

interface EditVendorIndustriesPayload {
  industries: LookupItem[]
}
export const editVendorIndustries = createAsyncThunk(
  'vendor/editVendorIndustries',
  async (data: EditVendorIndustriesPayload) => {
    await axiosInstance.put('/vendors/edit-vendor-industries', {
      industries: data.industries.map((industry) => industry.id),
    })
    return data
  },
)
interface SetRefDocumentsPayload {
  documents: GeneratedFile[]
}
export const setRefDocuments = createAsyncThunk(
  'vendor/setRefDocuments',
  async ({ documents }: SetRefDocumentsPayload): Promise<GeneratedFile[]> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put('/vendors/set-vendor-ref-docs', {
          documents,
        })
        .then(() => {
          return documents
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface GetVendorCompanyPayload {
  vendorId: string
}

export const getVendorCompany = createAsyncThunk(
  'vendor/vendorCompany',
  async ({ vendorId }: GetVendorCompanyPayload): Promise<VendorCompany> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<VendorCompany>(`/vendors/${vendorId}`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface RespondToVerificationPayload {
  vendorId: string
  status: VendorStatus
  note?: string
  retryDate?: string
}
export const respondToVerification = createAsyncThunk(
  'vendor/vendorRespondVerification',
  async ({ vendorId, status, note, retryDate }: RespondToVerificationPayload): Promise<string> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put(`/vendors/${vendorId}/respond-to-verification`, {
          status,
          note,
          retryDate,
        })
        .then(() => {
          resolve(status)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface GetDecisionDetailsPayload {
  vendorId: string
}

export const getDecisionDetailsCompany = createAsyncThunk(
  'vendor/decisionDetails',
  async ({ vendorId }: GetDecisionDetailsPayload): Promise<DecisionDetails> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<DecisionDetails>(`/vendors/${vendorId}/decision-details`)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

interface SetAdditionalDocumentsPayload {
  documents: GeneratedFile[]
}
export const setAdditionalDocuments = createAsyncThunk(
  'vendor/setAdditionalDocuments',
  async ({ documents }: SetAdditionalDocumentsPayload): Promise<GeneratedFile[]> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put('/vendors/set-vendor-other-docs', {
          documents,
        })
        .then(() => {
          return documents
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

export const getMyOffers = createAsyncThunk(
  'vendor/getMyOffers',
  async ({
    ipp,
    page,
  }: GetMyOffer): Promise<{
    page: number
    list: PaginatedResponse<MyOffer[]>
  }> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .get<PaginatedResponse<MyOffer[]>>(`/vendors/bid-list/me?&ipp=${ipp}&page=${page}`)
        .then((data) => {
          resolve({ page, list: data })
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

export const setAIRefDocuments = createAsyncThunk(
  'vendor/setAIRefDocuments',
  async ({ documents }: SetRefDocumentsPayload): Promise<GeneratedFile[]> => {
    return await new Promise((resolve, reject) => {
      axiosInstance
        .put('/vendors/set-vendor-ai-ref-docs', {
          documents,
        })
        .then(() => {
          return documents
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  },
)

export const sendAIVerificationRequest = createAsyncThunk('auth/requestAIVerification', async () => {
  await axios.put('/vendors/request-ai-verification')
})
